import { Router } from "@lib/router";
import { DeliveryMethod } from "@modules/order/constants";

import gtmEcommercePusher from "./gtmEcommercePusher";

export const CURRENCY_EUR = "EUR";

const LV_LOCALE = "lv";
const EE_LOCALE = "ee";

const getMarketLocale = (): string | undefined => {
  if (typeof window === "undefined") {
    return undefined;
  }
  try {
    return Router.defaultLocale;
  } catch {
    return undefined;
  }
};

export const isLvEnvironment = (): boolean => getMarketLocale() === LV_LOCALE;

export const isEeEnvironment = (): boolean => getMarketLocale() === EE_LOCALE;

// Ad-hoc pallet dimension titles: LV, EE
const PALLET_TITLES = ["palete", "alus"];

export const getParcelType = (packageSizeTitle?: string | null): string =>
  PALLET_TITLES.includes(packageSizeTitle?.trim().toLowerCase() ?? "") ? "Pallet" : "Parcel";

export type TDataLayerItem = {
  item_id: string;
  item_name: string;
  item_category: string;
  item_variant: string;
  item_category2: string;
  price: string;
  quantity: number;
};

export type TDataLayerUserData = {
  email?: string | null;
  phone?: string | null;
};

export const normalizeEmail = (email?: string | null): string | undefined => {
  if (!email) {
    return undefined;
  }
  const normalized = email.trim().toLowerCase();
  return normalized || undefined;
};

export const normalizePhone = (phone?: string | null): string | undefined => {
  if (!phone) {
    return undefined;
  }
  const digits = phone.replace(/\D/g, "");
  return digits ? `+${digits}` : undefined;
};

export const buildUserData = (email?: string | null, phone?: string | null): TDataLayerUserData => {
  const normalizedEmail = normalizeEmail(email);
  const normalizedPhone = normalizePhone(phone);

  return {
    ...(normalizedEmail && { email: normalizedEmail }),
    ...(normalizedPhone && { phone: normalizedPhone }),
  };
};

export const resetEcommerce = (): void => {
  gtmEcommercePusher({ ecommerce: null });
};

export const buildItem = (
  parcelType: string,
  deliveryMethod: string,
  destinationCountry: string,
  price: string
): TDataLayerItem => ({
  item_id: `${destinationCountry}-${parcelType}-${deliveryMethod}`.toUpperCase().replace(/\s+/g, "-"),
  item_name: `${parcelType} - ${deliveryMethod}`,
  item_category: parcelType,
  item_variant: deliveryMethod,
  item_category2: destinationCountry,
  price,
  quantity: 1,
});

export const getDeliveryMethodLabel = (deliveryMethod?: string | null): string => {
  switch (deliveryMethod) {
    case DeliveryMethod.PudoToPudo:
    case DeliveryMethod.DoorToPudo:
      return "Parcel locker";
    case DeliveryMethod.DoorToDoor:
    case DeliveryMethod.PudoToDoor:
      return "Courier";
    default:
      return "";
  }
};

